import { useState } from "react";
import { TableRow, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

interface ExpandableTableRowsProps<T> {
  rows: T[];
  previewCount?: number;
  renderRow: (row: T, index: number) => React.ReactNode;
  colSpan: number;
  moreLabel?: (count: number) => string;
}

export function ExpandableTableRows<T>({
  rows,
  previewCount = 5,
  renderRow,
  colSpan,
  moreLabel,
}: ExpandableTableRowsProps<T>) {
  const [expanded, setExpanded] = useState(false);

  const hiddenCount = Math.max(0, rows.length - previewCount);
  const visibleRows = expanded || hiddenCount === 0 ? rows : rows.slice(0, previewCount);

  const defaultMoreLabel = (n: number) => `View ${n} more`;
  const getMoreLabel = moreLabel ?? defaultMoreLabel;

  return (
    <>
      {visibleRows.map((row, index) => renderRow(row, index))}

      {!expanded && hiddenCount > 0 && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={colSpan} className="py-2 text-center border-t-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(true)}
              className="gap-1.5 text-muted-foreground h-8 text-xs"
              data-testid="button-expand-table-rows"
            >
              <ChevronDown className="h-3.5 w-3.5" />
              {getMoreLabel(hiddenCount)}
            </Button>
          </TableCell>
        </TableRow>
      )}

      {expanded && hiddenCount > 0 && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={colSpan} className="py-2 text-center border-t-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(false)}
              className="gap-1.5 text-muted-foreground h-8 text-xs"
              data-testid="button-collapse-table-rows"
            >
              <ChevronUp className="h-3.5 w-3.5" />
              Show less
            </Button>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
